import { SignatureGenerator } from "@scopely/withbuddies-web-signature-generator";
import type { AxiosResponse } from "axios";
import { differenceInMinutes } from "date-fns";
import { toast } from "react-toastify";
import { SESSIONTOKEN_EXPIRE_TIME_MINUTE } from "~/app/constants/providers";
import { useAuthStore } from "~/app/stores/auth";
import type { ApiRequestProps } from "./@types";
import { axiosApi } from "./axiosApi";
import { buildEndpoint } from "./buildEndpoint";
import { API_CONFIG, HEADERS_CONFIG } from "./config";
import type { TApiDefault } from "./types";
import {tracker} from "~/services/scopely/scopelyId.ts";

const isProduction = ["staging", "production"].includes(import.meta.env.VITE_APP_ENVIRONMENT);

interface TError {
  message: string;
}

export async function requestApi<T>({
  endpoint,
  method,
  id,
  params = {},
  body,
  timeout,
  headers = {},
  usePassSignature = true,
  c_platform = HEADERS_CONFIG.c_platform,
  c_store = HEADERS_CONFIG.c_store,
  c_locale = HEADERS_CONFIG.c_locale,
  c_version = HEADERS_CONFIG.c_version,
  c_bundle = HEADERS_CONFIG.c_bundle,
  c_titan_token = tracker.getDeviceToken(),
  showErrorNotification = true,
  useDefaultQueryParams = true,
}: ApiRequestProps): Promise<T> {
  const { logout } = useAuthStore.getState();

  try {
    let sessionToken = localStorage.getItem("sessionToken");

    if (sessionToken && isTokenInvalid()) {
      logout({});

      sessionToken = null;
    }

    const queryParams = { ...params };

    const defaultQueryParams = {
      ...(sessionToken ? { sessionToken } : {}),
      c_platform,
      c_store,
      c_locale,
      c_version,
      c_bundle,
      c_titan_token,
    };

    if (usePassSignature) {
      if (import.meta.env.VITE_APP_ENVIRONMENT === "dev") {
        queryParams.bypassSignature = true;
      } else if (API_CONFIG.bypassSignature !== undefined) {
        queryParams.bypassSignature = API_CONFIG.bypassSignature;
      }
    }

    const endpointWithParams = buildEndpoint(
      endpoint,
      useDefaultQueryParams ? { ...queryParams, ...defaultQueryParams } : queryParams,
      id,
    );

    const defaultHeaders: Record<string, string> = {
      c_platform: c_platform as string,
      accept: "application/json",
      "content-type": "application/json",
    };

    if (isProduction && usePassSignature) {
      const signatureGenerator = SignatureGenerator.getInstance().setUserToken(sessionToken ?? "");
      const bodyString = body ? JSON.stringify(body) : undefined;
      defaultHeaders["X-WithBuddies-Signature"] = await signatureGenerator.calculateSignature({
        path: endpoint,
        method,
        query: queryParams
          ? jsonToQueryParams(
              useDefaultQueryParams ? { ...queryParams, ...defaultQueryParams } : queryParams,
            )
          : "",
        body: bodyString,
      });
    }

    const bodyString = body ? JSON.stringify(body) : undefined;
    const response = await axiosApi.request({
      url: endpointWithParams,
      method,
      data: bodyString,
      timeout,
      headers: { ...defaultHeaders, ...headers },
    });

    throwIfResponseIsNotValid(response);

    return response.data;
  } catch (error: unknown) {
    if (showErrorNotification) {
      toast.error((error as TError).message, {
        toastId: "error-api",
        position: "top-right",
        autoClose: 4000,
        pauseOnHover: true,
        closeOnClick: true,
      });
    }
    throw error;
  }
}

const jsonToQueryParams = (data: Record<string, unknown>) => {
  return Object.keys(data)
    .map((key) => {
      return `${encodeURIComponent(key)}=${data[key]}`;
    })
    .join("&");
};

const isTokenInvalid = () => {
  const loginDateString = localStorage.getItem("loginDate");

  if (!loginDateString) {
    return true;
  }

  const loginDate = new Date(loginDateString);

  const isGreaterThan = differenceInMinutes(new Date(), loginDate);

  return isGreaterThan >= (SESSIONTOKEN_EXPIRE_TIME_MINUTE || 43200);
};

function throwIfResponseIsNotValid(responseAxios: AxiosResponse<TApiDefault>): void {
  const responseData = responseAxios?.data;

  if (!responseData?.IsSuccess) {
    toast.error(responseData?.Error?.Message || "An unexpected error occurred", {
      toastId: "error-api",
      position: "top-right",
      autoClose: 4000,
      pauseOnHover: true,
      closeOnClick: true,
    });

    throw new Error(responseData?.Error?.Message || "API request failed");
  }
}
